/**
 * useForecastData Hook
 * Fetches and manages forecast P&L data for the grid
 *
 * When versionId is provided, uses V3 Calculate API for on-demand calculations.
 * Otherwise falls back to legacy V2 API with pre-calculated values.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  fetchForecastPnLData,
  fetchForecastAccounts,
  saveForecastValue,
  saveForecastValuesBulk,
  saveOverrideV3,
  saveBulkOverridesV3,
  updateAccountDriver,
  updateDriverAssignmentV3,
  updateDriversBulk,
  clearMonthForecast,
  transformPnLDataToGrid,
  groupAccountsByType,
  calculateForecast,
  getLockedSnapshot,
  saveRoomStatOverride,
  type ForecastPnLRecord,
  type ForecastAccountRecord,
  type TransformedAccount,
  type SaveForecastValueParams,
  type UpdateDriverParams,
  type CalculationResult,
  type CalculationExplanationResult,
  type BulkDriverAssignment,
  type BulkDriverResponse,
  type LockedMonthSnapshot,
  type RoomStatType,
  saveDriverInputOverridesV3,
  clearDriverInputOverridesV3,
} from '../api';
import type { DriverType, DepartmentDriverType, ForecastAccount, MonthlyExpandedValues, ZeroBaseDriverLayer, LaborPositionConfig, DriverInputOverrideItem } from '../types';

// ============ Query Keys ============

export const forecastQueryKeys = {
  all: ['forecast-v3'] as const,
  pnlData: (orgId: number, locationId: number, year: number) =>
    [...forecastQueryKeys.all, 'pnl', { orgId, locationId, year }] as const,
  accounts: (orgId: number, locationId: number) =>
    [...forecastQueryKeys.all, 'accounts', { orgId, locationId }] as const,
  v3Calculate: (orgId: number, locationId: number, versionId: number, year: number) =>
    [...forecastQueryKeys.all, 'v3-calculate', { orgId, locationId, versionId, year }] as const,
  lockedSnapshots: (versionId: number, year: number, lockedMonths: number[]) =>
    [...forecastQueryKeys.all, 'locked-snapshots', { versionId, year, lockedMonths: lockedMonths.sort().join(',') }] as const,
};

// ============ Hook Parameters ============

interface UseForecastDataParams {
  orgId: number;
  locationId: number;
  year: number;
  versionId?: number | null; // When provided, uses V3 Calculate API
  requireVersion?: boolean; // Version pages must not start legacy reads while selection loads
  lockedMonths?: number[]; // Array of locked months (1-12) to fetch snapshots for
  enabled?: boolean;
  roomStatSource?: 'pms' | 'budget' | 'actuals'; // Source for room stats in calculations
  actualRollInEnabled?: boolean; // Closed months display actuals in the collapsed grid/annual totals
}

// ============ Helper: Generate month keys ============

function generateMonthKeys(year: number): string[] {
  return Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  });
}

function monthKeyToName(monthKey: string): string {
  const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const [, month] = monthKey.split('-');
  return monthNames[parseInt(month, 10) - 1];
}

function accountNumberSortValue(accountNumber?: string | null): number {
  const normalized = String(accountNumber || '').replace(/[^0-9.]/g, '');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function compareAccountNumbers(a?: string | null, b?: string | null): number {
  const numericDiff = accountNumberSortValue(a) - accountNumberSortValue(b);
  if (numericDiff !== 0) return numericDiff;
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true });
}

export function effectiveFinancialCloseDay(closeDay?: number | null): number {
  if (!Number.isFinite(closeDay || NaN)) return 15;
  return Math.min(Math.max(Math.trunc(closeDay as number), 1), 31);
}

export function closeDateForMonth(year: number, monthNumber: number, closeDay?: number | null): Date {
  const day = effectiveFinancialCloseDay(closeDay);
  const daysInCloseMonth = new Date(year, monthNumber + 1, 0).getDate();
  return new Date(year, monthNumber, Math.min(day, daysInCloseMonth), 0, 0, 0, 0);
}

/**
 * Most recent calendar month whose books are closed per the location's
 * financial close day (month m closes on day `closeDay` of month m+1).
 */
export function latestClosedMonth(closeDay?: number | null, now = new Date()): { year: number; month: number } {
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  for (let i = 0; i < 24; i++) {
    if (now >= closeDateForMonth(y, m, closeDay)) return { year: y, month: m };
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return { year: y, month: m };
}

function computeClosedMonthKeys(year: number, closeDay?: number | null, now = new Date()): Set<string> {
  const closed = new Set<string>();
  const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  for (let monthNumber = 1; monthNumber <= 12; monthNumber += 1) {
    if (now >= closeDateForMonth(year, monthNumber, closeDay)) {
      closed.add(monthNames[monthNumber - 1]);
    }
  }
  return closed;
}

function normalizeActualForDisplay(actual: number, accountType: string): number {
  return accountType === 'Revenue' ? actual * -1 : actual;
}

// ============ Return Types ============

interface UseForecastDataReturn {
  // Data
  accounts: ForecastAccount[];
  revenueAccounts: ForecastAccount[];
  expenseAccounts: ForecastAccount[];
  totals: {
    revenue: Record<string, number>;
    expense: Record<string, number>;
    netIncome: Record<string, number>;
  };
  rawRecords: ForecastPnLRecord[];
  closedMonthKeys: string[];
  financialCloseDay: number;
  roomStats: Record<string, {
    availableRooms: number;
    occupiedRooms: number;
    occupancyRate: number;
  }> | null;

  // Loading states
  isLoading: boolean;
  isRefetching: boolean;
  error: Error | null;

  // Mutations
  saveValue: (params: Omit<SaveForecastValueParams, 'orgId' | 'locationId'>) => Promise<void>;
  saveValuesBulk: (entries: Omit<SaveForecastValueParams, 'orgId' | 'locationId'>[]) => Promise<void>;
  updateDriver: (chartOfAccountId: number, driverType: DriverType, driverInput: number | null, selectedDepartments?: string[], clearOverrides?: boolean, fallbackDriverType?: 'Budget' | 'LY Same Month' | null, sourceChartOfAccountIds?: number[], zeroBaseDriverLayers?: ZeroBaseDriverLayer[], laborPositionConfigs?: LaborPositionConfig[]) => Promise<void>;
  updateDriversBulk: (assignments: BulkDriverAssignment[]) => Promise<BulkDriverResponse>;
  clearMonth: (chartOfAccountId: number, year: number, month: number) => Promise<void>;
  saveRoomStatOverride: (month: number, roomStatType: RoomStatType, value: number | null) => Promise<void>;
  saveDriverInputOverrides: (items: DriverInputOverrideItem[]) => Promise<{ applied: number; skippedLocked: { chartOfAccountId: number; year: number; month: number }[] }>;
  clearDriverInputOverrides: (chartOfAccountId?: number) => Promise<{ cleared: number }>;

  // Bulk operation loading state
  isBulkUpdating: boolean;

  // Actions
  refetch: () => void;
}

// ============ Main Hook ============

export function useForecastData({
  orgId,
  locationId,
  year,
  versionId,
  lockedMonths = [],
  enabled: requestedEnabled = true,
  requireVersion = false,
  roomStatSource = 'pms',
  actualRollInEnabled = true,
}: UseForecastDataParams): UseForecastDataReturn {
  const queryClient = useQueryClient();
  const useV3 = !!versionId;
  const enabled = requestedEnabled && (!requireVersion || useV3);
  const months = generateMonthKeys(year);

  // Full query key including roomStatSource — must be used consistently for optimistic updates
  const v3QueryKey = versionId
    ? [...forecastQueryKeys.v3Calculate(orgId, locationId, versionId, year), roomStatSource]
    : null;

  // Memoize lockedMonths to prevent unnecessary re-renders
  const lockedMonthsKey = lockedMonths.sort().join(',');

  // ============ V2 Data Fetching (for metadata + fallback) ============

  // Versions need only account metadata; Calculate supplies amounts and stats.
  // When in V3 mode, this provides metadata; when no version, it provides full data
  const {
    data: pnlData,
    isLoading: isPnlLoading,
    isRefetching: isPnlRefetching,
    error: pnlError,
    refetch: refetchPnl,
  } = useQuery({
    queryKey: [...forecastQueryKeys.pnlData(orgId, locationId, year), useV3 ? 'metadata' : 'full'],
    queryFn: () => fetchForecastPnLData({ orgId, locationId, year, ...(useV3 ? { metadataOnly: true } : {}) }),
    enabled: enabled && !!orgId && !!locationId && !!year, // Always fetch for metadata
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Legacy drivers are only used without a version. V3 returns version drivers.
  const {
    data: accountConfigs,
    isLoading: isAccountsLoading,
    error: accountsError,
  } = useQuery({
    queryKey: forecastQueryKeys.accounts(orgId, locationId),
    queryFn: () => fetchForecastAccounts({ orgId, locationId }),
    enabled: enabled && !useV3 && !!orgId && !!locationId,
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
  });

  // ============ V3 Calculate API ============

  // Get chart of account IDs from V2 P&L data (source of truth for all accounts)
  // We need to derive this from pnlData, not accountConfigs, because V3 uses version-scoped drivers
  // Memoized to prevent unnecessary re-renders and query re-fetches
  const chartOfAccountIds = useMemo(
    () => pnlData ? Array.from(new Set(pnlData
      .filter(r => ['revenue', 'expense'].includes((r.accountType || '').toLowerCase()) && r.department?.trim())
      .map(r => r.chartOfAccountId))) : [],
    [pnlData]
  );

  // V3 Calculate - on-demand calculations
  // Note: Query key uses versionId! (non-null assertion) since query is disabled when versionId is null
  const {
    data: v3Data,
    isLoading: isV3Loading,
    isRefetching: isV3Refetching,
    error: v3Error,
    refetch: refetchV3,
  } = useQuery({
    // Include roomStatSource in query key so data refetches when source changes
    queryKey: v3QueryKey!,
    queryFn: async () => {
      if (!versionId || chartOfAccountIds.length === 0) {
        return null;
      }

      return await calculateForecast({
        orgId,
        locationId,
        versionId,
        chartOfAccountIds,
        months,
        includeExplanation: true, // Enable explanations for "How X was calculated" display
        roomStatSource, // Pass room stat source for calculation
      });
    },
    enabled: enabled && useV3 && !!orgId && !!locationId && !!versionId && chartOfAccountIds.length > 0,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // ============ Locked Month Snapshots ============

  // Fetch locked month snapshots from Cosmos DB
  // These contain frozen values that should override calculated values
  const {
    data: lockedSnapshotsData,
    isLoading: isLockedSnapshotsLoading,
  } = useQuery({
    queryKey: forecastQueryKeys.lockedSnapshots(versionId!, year, lockedMonths),
    queryFn: async () => {
      if (!versionId || lockedMonths.length === 0) {
        return { snapshots: new Map<number, LockedMonthSnapshot>() };
      }

      // Fetch snapshots for all locked months in parallel
      const snapshotPromises = lockedMonths.map(month =>
        getLockedSnapshot({ versionId, year, month })
          .then(response => ({ month, response }))
          .catch(() => ({ month, response: null })) // Handle individual failures gracefully
      );

      const results = await Promise.all(snapshotPromises);

      // Build a map of month -> snapshot data
      const snapshots = new Map<number, LockedMonthSnapshot>();
      for (const { month, response } of results) {
        if (response?.locked && response.snapshot) {
          snapshots.set(month, response.snapshot);
        }
      }

      return { snapshots };
    },
    enabled: enabled && useV3 && !!versionId && lockedMonths.length > 0,
    staleTime: 60000, // 1 minute - locked data doesn't change often
    refetchOnWindowFocus: false,
  });

  // Build a lookup map for quick access to locked account values
  // Key: "chartOfAccountId:monthName" -> LockedAccountSnapshot
  const lockedAccountsMap = useMemo(() => {
    const map = new Map<string, { calculatedAmount: number; driverType: string; driverInput: number | null }>();
    if (!lockedSnapshotsData?.snapshots) return map;

    const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

    lockedSnapshotsData.snapshots.forEach((snapshot, month) => {
      const monthName = monthNames[month - 1];
      for (const account of snapshot.accounts) {
        const key = `${account.chartOfAccountId}:${monthName}`;
        map.set(key, {
          calculatedAmount: account.calculatedAmount,
          driverType: account.driverType,
          driverInput: account.driverInput,
        });
      }
    });

    return map;
  }, [lockedSnapshotsData]);

  // Build locked room stats map: monthKey -> roomStats from snapshot
  const lockedRoomStatsMap = useMemo(() => {
    const map = new Map<string, { availableRooms: number; occupiedRooms: number; occupancyRate: number }>();
    if (!lockedSnapshotsData?.snapshots) return map;

    lockedSnapshotsData.snapshots.forEach((snapshot, month) => {
      if (snapshot.roomStats) {
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        map.set(monthKey, snapshot.roomStats);
      }
    });

    return map;
  }, [lockedSnapshotsData, year]);

  // ============ Data Transformation ============

  const financialCloseDay = v3Data?.financialCloseDay ?? v3Data?.version?.financialCloseDay ?? 15;
  const closedMonthKeys = useMemo(
    () => computeClosedMonthKeys(year, financialCloseDay),
    [year, financialCloseDay]
  );

  // Transform legacy V2 data for grid
  const transformedData = !useV3 && pnlData ? transformPnLDataToGrid(pnlData) : null;

  // Transform V3 data if available
  let accountsWithConfigs: Map<number, TransformedAccount>;

  if (useV3 && v3Data?.results) {
    // Build accounts from V3 calculation results, using V2 data for metadata
    // Pass locked accounts map to override values for locked months
    accountsWithConfigs = transformV3ResultsToAccounts(
      v3Data.results,
      accountConfigs || [],
      pnlData || [],
      v3Data.explanations,
      lockedAccountsMap,
      actualRollInEnabled,
      closedMonthKeys
    );
  } else if (transformedData) {
    // Use legacy V2 data
    accountsWithConfigs = mergeAccountConfigs(transformedData.accounts, accountConfigs || []);
  } else {
    accountsWithConfigs = new Map<number, TransformedAccount>();
  }

  // Group by type
  const grouped = groupAccountsByType(accountsWithConfigs);

  // Convert to ForecastAccount format for components
  const allAccounts = convertToForecastAccounts(accountsWithConfigs);
  const revenueAccounts = convertToForecastAccounts(
    new Map(grouped.revenue.map(a => [a.chartOfAccountId, a]))
  );
  const expenseAccounts = convertToForecastAccounts(
    new Map(grouped.expense.map(a => [a.chartOfAccountId, a]))
  );

  // ============ Mutations ============

  // Helper to invalidate appropriate queries based on mode
  const invalidateDataQueries = () => {
    // Always invalidate P&L data for metadata freshness (names, types, departments)
    queryClient.invalidateQueries({
      queryKey: forecastQueryKeys.pnlData(orgId, locationId, year),
    });
    // Also invalidate V3 calculate cache if in V3 mode
    if (useV3 && v3QueryKey) {
      queryClient.invalidateQueries({
        queryKey: v3QueryKey,
      });
    }
  };

  // Save single forecast value - uses V3 override API when version is selected
  // Uses optimistic updates to prevent UI flash
  const saveValueMutation = useMutation({
    mutationFn: async (params: SaveForecastValueParams & { versionId?: number }) => {
      if (params.versionId) {
        // V3: Save as version-scoped override
        return saveOverrideV3({
          versionId: params.versionId,
          chartOfAccountId: params.chartOfAccountId,
          year: params.year,
          month: params.month,
          overrideAmount: params.forecastAmount,
        });
      } else {
        // V2: Save to location-scoped forecast storage
        return saveForecastValue(params);
      }
    },
    // Optimistic update: immediately update cache before API call completes
    onMutate: async (params) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      if (params.versionId) {
        await queryClient.cancelQueries({
          queryKey: [...forecastQueryKeys.v3Calculate(orgId, locationId, params.versionId, year), roomStatSource],
        });

        // Snapshot previous value for rollback
        const previousV3Data = queryClient.getQueryData<{ results: CalculationResult[] }>(
          [...forecastQueryKeys.v3Calculate(orgId, locationId, params.versionId, year), roomStatSource]
        );

        // Optimistically update V3 cache
        queryClient.setQueryData(
          [...forecastQueryKeys.v3Calculate(orgId, locationId, params.versionId, year), roomStatSource],
          (old: { results: CalculationResult[] } | undefined) => {
            if (!old?.results) return old;
            return {
              ...old,
              results: old.results.map(result => {
                if (result.chartOfAccountId === params.chartOfAccountId &&
                    result.month === `${params.year}-${String(params.month).padStart(2, '0')}`) {
                  return {
                    ...result,
                    calculatedAmount: params.forecastAmount,
                    hasOverride: true,  // Mark as having override
                    overrideAmount: params.forecastAmount,
                    source: 'override' as const,
                  };
                }
                return result;
              }),
            };
          }
        );

        return { previousV3Data, versionId: params.versionId };
      }

      return {};
    },
    // Rollback on error
    onError: (err, params, context) => {
      if (params.versionId && context?.previousV3Data) {
        queryClient.setQueryData(
          [...forecastQueryKeys.v3Calculate(orgId, locationId, params.versionId, year), roomStatSource],
          context.previousV3Data
        );
      }
    },
    // Invalidate on success to recalculate dependent accounts (e.g., expenses that depend on revenue)
    // The optimistic update provides immediate feedback, but we need fresh data for dependencies
    onSuccess: (_, params) => {
      if (params.versionId) {
        // Invalidate V3 calculate to trigger full recalculation including dependent accounts
        queryClient.invalidateQueries({
          queryKey: [...forecastQueryKeys.v3Calculate(orgId, locationId, params.versionId, year), roomStatSource],
        });
      }
    },
  });

  // Save multiple forecast values - uses V3 override API when version is selected
  const saveValuesBulkMutation = useMutation({
    mutationFn: async (entries: (SaveForecastValueParams & { versionId?: number })[]) => {
      if (entries.length > 0 && entries[0].versionId) {
        // V3: Bulk save as version-scoped overrides in a single request
        await saveBulkOverridesV3(entries[0].versionId!, entries.map(entry => ({
          chartOfAccountId: entry.chartOfAccountId,
          year: entry.year,
          month: entry.month,
          overrideAmount: entry.forecastAmount,
        })));
      } else {
        // V2: Bulk save to location-scoped forecast storage
        return saveForecastValuesBulk(entries);
      }
    },
    // Optimistic update: update cache SYNCHRONOUSLY first, then cancel in-flight queries
    onMutate: async (entries) => {
      const vid = entries[0]?.versionId;
      if (!vid) return {};

      // Snapshot previous value for rollback BEFORE modifying
      const previousV3Data = queryClient.getQueryData<{ results: CalculationResult[] }>(
        [...forecastQueryKeys.v3Calculate(orgId, locationId, vid, year), roomStatSource]
      );

      // Build a lookup for fast matching: "chartOfAccountId-YYYY-MM" → forecastAmount
      const updateMap = new Map<string, number>();
      for (const entry of entries) {
        const key = `${entry.chartOfAccountId}-${entry.year}-${String(entry.month).padStart(2, '0')}`;
        updateMap.set(key, entry.forecastAmount);
      }

      // Update cache IMMEDIATELY (synchronous) — this triggers React re-render
      queryClient.setQueryData(
        [...forecastQueryKeys.v3Calculate(orgId, locationId, vid, year), roomStatSource],
        (old: { results: CalculationResult[] } | undefined) => {
          if (!old?.results) return old;
          return {
            ...old,
            results: old.results.map(result => {
              const key = `${result.chartOfAccountId}-${result.month}`;
              const newAmount = updateMap.get(key);
              if (newAmount !== undefined) {
                return {
                  ...result,
                  calculatedAmount: newAmount,
                  hasOverride: true,
                  overrideAmount: newAmount,
                  source: 'override' as const,
                };
              }
              return result;
            }),
          };
        }
      );

      // Cancel in-flight queries AFTER cache update so they don't overwrite our optimistic data
      await queryClient.cancelQueries({
        queryKey: [...forecastQueryKeys.v3Calculate(orgId, locationId, vid, year), roomStatSource],
      });

      return { previousV3Data, versionId: vid };
    },
    onError: (err, entries, context) => {
      if (context?.versionId && context?.previousV3Data) {
        queryClient.setQueryData(
          [...forecastQueryKeys.v3Calculate(orgId, locationId, context.versionId, year), roomStatSource],
          context.previousV3Data
        );
      }
    },
    onSuccess: invalidateDataQueries,
  });

  // Update driver - uses V3 API when version is selected, otherwise V2
  const updateDriverMutation = useMutation({
    mutationFn: async (params: UpdateDriverParams & { versionId?: number; clearOverrides?: boolean; fallbackDriverType?: 'Budget' | 'LY Same Month' | null; sourceChartOfAccountIds?: number[]; zeroBaseDriverLayers?: ZeroBaseDriverLayer[]; laborPositionConfigs?: LaborPositionConfig[] }) => {
      if (params.versionId) {
        // V3: Update version-scoped driver assignment
        return updateDriverAssignmentV3({
          versionId: params.versionId,
          chartOfAccountId: params.chartOfAccountId,
          driverType: params.driverType,
          driverInput: params.driverInput,
          selectedDepartments: params.selectedDepartments,
          clearOverrides: params.clearOverrides,
          fallbackDriverType: params.fallbackDriverType,
          sourceChartOfAccountIds: params.sourceChartOfAccountIds,
          zeroBaseDriverLayers: params.zeroBaseDriverLayers,
          laborPositionConfigs: params.laborPositionConfigs,
        });
      } else {
        // V2: Update location-scoped account config
        return updateAccountDriver(params);
      }
    },
    onSuccess: () => {
      // Invalidate account configs
      queryClient.invalidateQueries({
        queryKey: forecastQueryKeys.accounts(orgId, locationId),
      });
      // Invalidate calculation data (this triggers recalculation for V3)
      invalidateDataQueries();
    },
  });

  // Clear month - uses V3 override API (with null amount) when version is selected
  const clearMonthMutation = useMutation({
    mutationFn: async (params: { chartOfAccountId: number; year: number; month: number; versionId?: number }) => {
      if (params.versionId) {
        // V3: Clear by saving override with null amount
        return saveOverrideV3({
          versionId: params.versionId,
          chartOfAccountId: params.chartOfAccountId,
          year: params.year,
          month: params.month,
          overrideAmount: null, // null clears the override
        });
      } else {
        // V2: Clear from location-scoped forecast storage
        return clearMonthForecast({ orgId, locationId, ...params });
      }
    },
    onSuccess: invalidateDataQueries,
  });

  // Driver-level monthly overrides (rate / layer value / position wage) — V3 only
  const driverInputOverridesMutation = useMutation({
    mutationFn: async (items: DriverInputOverrideItem[]) => {
      if (!versionId) throw new Error('Driver overrides require a version to be selected');
      return saveDriverInputOverridesV3(versionId, items);
    },
    onSuccess: invalidateDataQueries,
  });
  const clearDriverInputOverridesMutation = useMutation({
    mutationFn: async (chartOfAccountId?: number) => {
      if (!versionId) throw new Error('Driver overrides require a version to be selected');
      return clearDriverInputOverridesV3(versionId, chartOfAccountId);
    },
    onSuccess: invalidateDataQueries,
  });

  // Bulk driver update - V3 only
  const updateDriversBulkMutation = useMutation({
    mutationFn: async (assignments: BulkDriverAssignment[]): Promise<BulkDriverResponse> => {
      if (!versionId) {
        throw new Error('Bulk driver updates require a version to be selected');
      }
      return updateDriversBulk({
        versionId,
        assignments,
      });
    },
    onSuccess: () => {
      // Invalidate account configs
      queryClient.invalidateQueries({
        queryKey: forecastQueryKeys.accounts(orgId, locationId),
      });
      // Invalidate calculation data (triggers recalculation)
      invalidateDataQueries();
    },
  });

  // Room stat override mutation - V3 only
  const roomStatOverrideMutation = useMutation({
    mutationFn: async (params: { month: number; roomStatType: RoomStatType; value: number | null }) => {
      if (!versionId) {
        throw new Error('Room stat overrides require a version to be selected');
      }
      return saveRoomStatOverride({
        versionId,
        year,
        month: params.month,
        roomStatType: params.roomStatType,
        overrideValue: params.value,
      });
    },
    onSuccess: () => {
      // Invalidate V3 calculate cache to refresh room stats
      if (versionId) {
        queryClient.invalidateQueries({
          queryKey: v3QueryKey!,
        });
      }
    },
  });

  // ============ Action Handlers ============

  const saveValue = async (params: Omit<SaveForecastValueParams, 'orgId' | 'locationId'>) => {
    await saveValueMutation.mutateAsync({
      orgId,
      locationId,
      ...params,
      versionId: versionId || undefined, // Pass versionId to use V3 override API
    });
  };

  const saveValuesBulk = async (entries: Omit<SaveForecastValueParams, 'orgId' | 'locationId'>[]) => {
    const fullEntries = entries.map(entry => ({
      orgId,
      locationId,
      ...entry,
      versionId: versionId || undefined, // Pass versionId to use V3 override API
    }));
    await saveValuesBulkMutation.mutateAsync(fullEntries);
  };

  const updateDriver = async (chartOfAccountId: number, driverType: DriverType, driverInput: number | null, selectedDepartments?: string[], clearOverrides?: boolean, fallbackDriverType?: 'Budget' | 'LY Same Month' | null, sourceChartOfAccountIds?: number[], zeroBaseDriverLayers?: ZeroBaseDriverLayer[], laborPositionConfigs?: LaborPositionConfig[]) => {
    await updateDriverMutation.mutateAsync({
      orgId,
      locationId,
      chartOfAccountId,
      driverType,
      driverInput,
      selectedDepartments,
      clearOverrides,
      fallbackDriverType,
      sourceChartOfAccountIds,
      zeroBaseDriverLayers,
      laborPositionConfigs,
      versionId: versionId || undefined,
    });
  };

  const clearMonth = async (chartOfAccountId: number, year: number, month: number) => {
    await clearMonthMutation.mutateAsync({
      chartOfAccountId,
      year,
      month,
      versionId: versionId || undefined, // Pass versionId to use V3 override API
    });
  };

  const saveDriverInputOverrides = async (items: DriverInputOverrideItem[]) => {
    return await driverInputOverridesMutation.mutateAsync(items);
  };

  const clearDriverInputOverrides = async (chartOfAccountId?: number) => {
    return await clearDriverInputOverridesMutation.mutateAsync(chartOfAccountId);
  };

  const updateDriversBulkAction = async (assignments: BulkDriverAssignment[]): Promise<BulkDriverResponse> => {
    return await updateDriversBulkMutation.mutateAsync(assignments);
  };

  const saveRoomStatOverrideValue = async (
    month: number,
    roomStatType: RoomStatType,
    value: number | null
  ) => {
    await roomStatOverrideMutation.mutateAsync({ month, roomStatType, value });
  };

  // Calculate totals from accounts
  const totals = calculateTotals(allAccounts);

  // Extract room stats from V3 response when available, then overlay locked snapshot values
  const roomStats = useMemo(() => {
    if (!useV3 || !v3Data?.roomStats) return null;

    // Clone and override locked months with snapshot values
    const merged = { ...v3Data.roomStats };
    lockedRoomStatsMap.forEach((lockedStats, monthKey) => {
      if (merged[monthKey]) {
        merged[monthKey] = {
          ...merged[monthKey],
          availableRooms: lockedStats.availableRooms,
          occupiedRooms: lockedStats.occupiedRooms,
          occupancyRate: lockedStats.occupancyRate,
        };
      } else {
        merged[monthKey] = lockedStats as typeof merged[string];
      }
    });

    if (actualRollInEnabled) {
      Object.entries(merged).forEach(([monthKey, stats]) => {
        const monthName = monthKeyToName(monthKey);
        if (!closedMonthKeys.has(monthName)) return;
        const actual = stats.actual;
        if (!actual) return;
        // Roll actuals in for closed months, but a property with missing
        // GL stat actuals (0s) keeps the resolved projection instead of
        // zeroing the occupancy line.
        const rolledAvailable = actual.availableRooms || stats.availableRooms || 0;
        const rolledOccupied = actual.occupiedRooms || stats.occupiedRooms || 0;
        merged[monthKey] = {
          ...stats,
          availableRooms: rolledAvailable,
          occupiedRooms: rolledOccupied,
          occupancyRate: rolledAvailable > 0 ? (rolledOccupied / rolledAvailable) * 100 : 0,
        };
      });
    }

    return merged;
  }, [useV3, v3Data?.roomStats, lockedRoomStatsMap, actualRollInEnabled, closedMonthKeys]);

  return {
    // Data
    accounts: allAccounts,
    revenueAccounts,
    expenseAccounts,
    totals: useV3 ? totals : (transformedData?.totals || { revenue: {}, expense: {}, netIncome: {} }),
    rawRecords: useV3 ? [] : (pnlData || []),
    closedMonthKeys: Array.from(closedMonthKeys),
    financialCloseDay,
    roomStats,

    // A version does not wait for legacy driver configuration.
    isLoading: useV3
      ? (isV3Loading || isPnlLoading || (lockedMonths.length > 0 && isLockedSnapshotsLoading))
      : (isPnlLoading || isAccountsLoading),
    isRefetching: useV3 ? (isV3Refetching || isPnlRefetching) : isPnlRefetching,
    error: (useV3 ? (v3Error || pnlError) : (pnlError || accountsError)) as Error | null,

    // Mutations
    saveValue,
    saveValuesBulk,
    updateDriver,
    updateDriversBulk: updateDriversBulkAction,
    clearMonth,
    saveRoomStatOverride: saveRoomStatOverrideValue,
    saveDriverInputOverrides,
    clearDriverInputOverrides,

    // Bulk operation loading state
    isBulkUpdating: updateDriversBulkMutation.isPending,

    // Actions
    refetch: () => {
      if (!enabled) return;
      return useV3 ? refetchV3() : refetchPnl();
    },
  };
}

// ============ V3 Transform Helper ============

/**
 * Transform V3 calculation results to TransformedAccount map
 * Uses pnlData for account metadata (names, types, departments)
 * Optionally overrides values with locked snapshot data for locked months
 */
function transformV3ResultsToAccounts(
  results: CalculationResult[],
  accountConfigs: ForecastAccountRecord[],
  pnlData: ForecastPnLRecord[],
  explanations?: CalculationExplanationResult[],
  lockedAccountsMap?: Map<string, { calculatedAmount: number; driverType: string; driverInput: number | null }>,
  actualRollInEnabled = true,
  closedMonthKeys: Set<string> = new Set()
): Map<number, TransformedAccount> {
  const accounts = new Map<number, TransformedAccount>();

  // Build metadata lookup from V2 P&L data
  const metadataMap = new Map<number, {
    accountNumber: string;
    accountName: string;
    accountType: string;
    department: string;
    departmentId?: number;
  }>();

  for (const record of pnlData) {
    if (!metadataMap.has(record.chartOfAccountId)) {
      metadataMap.set(record.chartOfAccountId, {
        accountNumber: record.accountNumber || '',
        accountName: record.accountName || '',
        accountType: record.accountType || 'Expense',
        department: record.department || record.accountGroup || 'Other',
        departmentId: record.departmentId,
      });
    }
  }

  // Build explanation factors lookup: key = "accountId:month"
  // Include factors, steps, and formula from the API response
  const explanationMap = new Map<string, Record<string, unknown>>();
  if (explanations) {
    for (const exp of explanations) {
      const key = `${exp.chartOfAccountId}:${exp.month}`;
      // Merge factors with steps and formula for complete explanation
      explanationMap.set(key, {
        ...(exp.factors || {}),
        steps: exp.steps || [],
        formula: exp.formula || '',
      });
    }
  }

  // Group results by account
  for (const result of results) {
    const accountId = result.chartOfAccountId;

    if (!accounts.has(accountId)) {
      // Get metadata from V2 data
      const metadata = metadataMap.get(accountId);

      // Only include Revenue and Expense accounts in P&L view (like budgeting module)
      const rawAccountType = (metadata?.accountType || '').toLowerCase();
      const isRevenue = rawAccountType === 'revenue';
      const isExpense = rawAccountType === 'expense';
      if (!isRevenue && !isExpense) {
        continue; // Skip non-P&L accounts (Asset, Liability, Equity, etc.)
      }

      // Filter out accounts with null department (like budgeting module)
      if (!metadata?.department) {
        continue;
      }

      accounts.set(accountId, {
        id: accountId,
        chartOfAccountId: accountId,
        accountNumber: metadata?.accountNumber || '',
        accountName: metadata?.accountName || `Account ${accountId}`,
        accountType: isRevenue ? 'Revenue' : 'Expense',
        department: metadata?.department || 'Other',
        departmentId: metadata?.departmentId,
        driverType: (result.driverType || 'Manual') as DriverType,
        driverInput: result.driverInput,
        selectedDepartments: result.selectedDepartments || [], // For % of Selected Revenue driver
        sourceChartOfAccountIds: result.sourceChartOfAccountIds || [], // For % of Account driver
        zeroBaseDriverLayers: result.zeroBaseDriverLayers || undefined, // For Zero Base composable driver
        laborPositionConfigs: result.laborPositionConfigs || undefined, // For Labor Config composable driver
        monthlyValues: {},
        explanationFactors: {}, // Initialize explanation factors
        monthlyOverrides: {}, // Track which months have overrides
        monthlyExpanded: {}, // Budget/Actual/Forecast expanded values per month
        total: 0,
        // Adjustment tracking (aggregated from all months)
        hasAdjustments: false,
        adjustmentsCount: 0,
        adjustmentsTotal: 0,
        // Driver-level monthly overrides
        monthlyDriverRate: {},
        monthlyZeroBaseLayers: {},
        monthlyLaborConfigs: {},
        monthlyDriverInputOverrides: {},
        driverInputOverrideCount: 0,
      });
    }

    const account = accounts.get(accountId);
    if (!account) {
      continue; // Account was filtered out (non-P&L type or no department)
    }

    const monthName = monthKeyToName(result.month);

    // Check if there's a locked value for this account/month
    const lockedKey = `${accountId}:${monthName}`;
    const lockedValue = lockedAccountsMap?.get(lockedKey);

    // Get budget, actual, and LY actual from the calculation result
    const budget = result.budget || 0;
    const actual = result.actualAmount || 0;
    const lyActual = result.lyActual || 0;
    const forecast = result.calculatedAmount || 0;
    const hasOverride = result.hasOverride === true;
    // baseAmount is the driver value BEFORE adjustments are applied
    const baseAmount = result.baseAmount ?? forecast;
    const monthHasAdjustments = result.hasAdjustments === true;
    const monthAdjustmentsTotal = result.adjustmentsTotal || 0;
    const isClosedMonth = closedMonthKeys.has(monthName);
    const shouldRollActual = actualRollInEnabled && isClosedMonth;
    const actualDisplay = normalizeActualForDisplay(actual, account.accountType);

    if (lockedValue) {
      const displayValue = shouldRollActual ? actualDisplay : lockedValue.calculatedAmount;

      // Use locked (frozen) value as the forecast basis; closed months can
      // still roll in actuals for the blended workspace view.
      account.monthlyValues[monthName] = displayValue;
      account.total += displayValue;

      // Mark explanation as "Locked" for locked months
      if (account.explanationFactors) {
        account.explanationFactors[monthName] = {
          isLocked: true,
          formula: 'Locked snapshot value',
          steps: ['This month is locked - showing frozen value from snapshot'],
        };
      }

      // Populate monthlyExpanded with locked values
      if (account.monthlyExpanded) {
        account.monthlyExpanded[monthName] = {
          forecast: lockedValue.calculatedAmount,
          baseAmount: lockedValue.calculatedAmount, // For locked months, base = final
          budget,
          actual,
          lyActual,
          lyBudget: result.lyBudget,
          lyBudgetOccupiedRooms: result.lyBudgetOccupiedRooms,
          current: displayValue,
          isActualRolledIn: shouldRollActual,
          hasOverride: false,
          hasAdjustments: false,
          adjustmentsTotal: 0,
        };
      }
    } else {
      const displayValue = shouldRollActual ? actualDisplay : forecast;

      // Collapsed view uses actuals for financially closed months in
      // Actuals + Forecast mode, and forecast for all months in Forecast only.
      account.monthlyValues[monthName] = displayValue;
      account.total += displayValue;

      // Track if this month has an override
      if (account.monthlyOverrides) {
        account.monthlyOverrides[monthName] = hasOverride;
      }

      // Ineligible driver (calculator refused it — e.g. revenue account on
      // % of Total Revenue): surface the reason on the account.
      if ((result as { driverError?: string | null }).driverError) {
        account.driverError = (result as { driverError?: string | null }).driverError as string;
      }

      // Track daily values + pin-blend mode for this month
      if (result.hasDailyValues) {
        if (!account.monthlyDailyValues) account.monthlyDailyValues = {};
        account.monthlyDailyValues[monthName] = true;
        if (!account.monthlyDailyMode) account.monthlyDailyMode = {};
        account.monthlyDailyMode[monthName] = result.dailyValuesMode === 'blended' ? 'blended' : 'legacy';
        if (!account.monthlyPinnedCounts) account.monthlyPinnedCounts = {};
        account.monthlyPinnedCounts[monthName] = result.pinnedDaysCount ?? 0;
      }

      // Populate monthlyExpanded with all values
      if (account.monthlyExpanded) {
        account.monthlyExpanded[monthName] = {
          forecast,
          baseAmount,
          budget,
          actual,
          lyActual,
          lyBudget: result.lyBudget,
          lyBudgetOccupiedRooms: result.lyBudgetOccupiedRooms,
          current: displayValue,
          isActualRolledIn: shouldRollActual,
          hasOverride,
          hasAdjustments: monthHasAdjustments,
          adjustmentsTotal: monthAdjustmentsTotal,
        };
      }

      // Capture explanation factors for this month
      const expKey = `${accountId}:${result.month}`;
      const factors = explanationMap.get(expKey);
      if (factors && account.explanationFactors) {
        account.explanationFactors[monthName] = factors;
      }

      // Aggregate adjustment data from this month's result
      if (result.hasAdjustments) {
        account.hasAdjustments = true;
        // Count unique adjustments - the adjustments array contains each applied adjustment
        if (result.adjustments && result.adjustments.length > 0) {
          account.adjustmentsCount = (account.adjustmentsCount || 0) + result.adjustments.length;
        }
        // Sum up the adjustment totals across months
        if (result.adjustmentsTotal) {
          account.adjustmentsTotal = (account.adjustmentsTotal || 0) + result.adjustmentsTotal;
        }
      }
    }

    // Driver-level monthly overrides — effective rate / configs for this month
    if (!lockedValue) {
      if (account.monthlyDriverRate) account.monthlyDriverRate[monthName] = result.driverRate ?? null;
      if (account.monthlyZeroBaseLayers && result.effectiveZeroBaseDriverLayers) {
        account.monthlyZeroBaseLayers[monthName] = result.effectiveZeroBaseDriverLayers;
      }
      if (account.monthlyLaborConfigs && result.effectiveLaborPositionConfigs) {
        account.monthlyLaborConfigs[monthName] = result.effectiveLaborPositionConfigs;
      }
      const ovRows = result.driverInputOverrides || [];
      if (account.monthlyDriverInputOverrides) account.monthlyDriverInputOverrides[monthName] = ovRows;
      account.driverInputOverrideCount = (account.driverInputOverrideCount || 0) + ovRows.length;
    }

    // Update driver info (use locked driver if available, otherwise calculated)
    if (lockedValue) {
      account.driverType = lockedValue.driverType as DriverType;
      account.driverInput = lockedValue.driverInput;
    } else if (result.driverType) {
      account.driverType = result.driverType as DriverType;
      account.driverInput = result.driverInput;
    }

    // Update department driver inheritance info (always capture these values)
    account.inheritsFromDepartment = result.inheritsFromDepartment ?? false;
    account.departmentDriverType = result.departmentDriverType ?? null;
    account.driverSource = result.driverSource ?? 'none';
    account.usingFallbackDriver = result.usingFallbackDriver ?? false;
    account.originalDepartmentDriverType = result.originalDepartmentDriverType ?? null;
  }

  return accounts;
}

/**
 * Calculate totals from accounts
 */
function calculateTotals(accounts: ForecastAccount[]): {
  revenue: Record<string, number>;
  expense: Record<string, number>;
  netIncome: Record<string, number>;
} {
  const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  const totals = {
    revenue: {} as Record<string, number>,
    expense: {} as Record<string, number>,
    netIncome: {} as Record<string, number>,
  };

  // Initialize
  for (const month of monthKeys) {
    totals.revenue[month] = 0;
    totals.expense[month] = 0;
    totals.netIncome[month] = 0;
  }

  // Sum by account type
  for (const account of accounts) {
    for (const month of monthKeys) {
      const value = account.monthlyValues[month] || 0;
      if (account.accountType === 'Revenue') {
        totals.revenue[month] += value;
      } else {
        totals.expense[month] += value;
      }
    }
  }

  // Calculate net income
  for (const month of monthKeys) {
    totals.netIncome[month] = totals.revenue[month] - totals.expense[month];
  }

  return totals;
}

// ============ Helper Functions ============

/**
 * Merge account configurations (drivers) with P&L data
 */
function mergeAccountConfigs(
  accounts: Map<number, TransformedAccount>,
  configs: ForecastAccountRecord[]
): Map<number, TransformedAccount> {
  const configMap = new Map(configs.map(c => [c.chartOfAccountId, c]));

  accounts.forEach((account, id) => {
    const config = configMap.get(id);
    if (config) {
      account.driverType = config.forecastDriver as DriverType || 'Manual';
    }
  });

  return accounts;
}

/**
 * Convert TransformedAccount Map to ForecastAccount array
 */
function convertToForecastAccounts(accounts: Map<number, TransformedAccount>): ForecastAccount[] {
  const result: ForecastAccount[] = [];

  accounts.forEach(account => {
    result.push({
      id: account.id,
      chartOfAccountId: account.chartOfAccountId,
      accountNumber: account.accountNumber,
      accountName: account.accountName,
      accountType: account.accountType,
      department: account.department,
      departmentId: account.departmentId,
      driverType: account.driverType,
      driverInput: account.driverInput,
      selectedDepartments: account.selectedDepartments,
      sourceChartOfAccountIds: account.sourceChartOfAccountIds,
      zeroBaseDriverLayers: account.zeroBaseDriverLayers,
      laborPositionConfigs: account.laborPositionConfigs,
      monthlyValues: account.monthlyValues as ForecastAccount['monthlyValues'],
      explanationFactors: account.explanationFactors as ForecastAccount['explanationFactors'],
      monthlyOverrides: account.monthlyOverrides as ForecastAccount['monthlyOverrides'],
      monthlyDailyValues: account.monthlyDailyValues as ForecastAccount['monthlyDailyValues'],
      monthlyDailyMode: account.monthlyDailyMode as ForecastAccount['monthlyDailyMode'],
      monthlyPinnedCounts: account.monthlyPinnedCounts as ForecastAccount['monthlyPinnedCounts'],
      monthlyExpanded: account.monthlyExpanded as ForecastAccount['monthlyExpanded'],
      monthlyDriverRate: account.monthlyDriverRate,
      monthlyZeroBaseLayers: account.monthlyZeroBaseLayers,
      monthlyLaborConfigs: account.monthlyLaborConfigs,
      monthlyDriverInputOverrides: account.monthlyDriverInputOverrides,
      driverInputOverrideCount: account.driverInputOverrideCount ?? 0,
      driverError: (account as { driverError?: string | null }).driverError ?? null,
      total: account.total,
      // Adjustment data
      hasAdjustments: account.hasAdjustments,
      adjustmentsCount: account.adjustmentsCount,
      adjustmentsTotal: account.adjustmentsTotal,
      // Department driver inheritance info
      inheritsFromDepartment: account.inheritsFromDepartment,
      departmentDriverType: account.departmentDriverType as DepartmentDriverType | null | undefined,
      driverSource: account.driverSource,
      usingFallbackDriver: account.usingFallbackDriver,
      originalDepartmentDriverType: account.originalDepartmentDriverType as DepartmentDriverType | null | undefined,
    });
  });

  // Sort by account number
  result.sort((a, b) => compareAccountNumbers(a.accountNumber, b.accountNumber));

  return result;
}

export default useForecastData;
